// #region Setup

import { useReducer, useContext, createContext, useCallback } from 'react';
import { GoMotoComponent } from '../../API/GoMoto.types';
import {
    AdminFiltersContextState,
    AdminFiltersStateContextType,
    AdminFiltersState,
    AdminFiltersDispatchContextType,
    AdminFiltersDispatch,
    AdminFiltersReducerFunction,
    AdminFiltersDispatchTypes,
    AdminFiltersProviderProps,
    AdminFiltersStateHook,
    AdminFiltersDispatchHook,
    AdminFiltersFetchAppointmentsHook,
} from './AdminFilters.types';
import { AppointmentType } from '../../API/Enums';
import { getActiveAppointments, getDmsAppointments, getPriorityAppointments, IAppointment } from '../../API/GoMoto';
import { useKioskState } from '../Kiosk/kiosk';
import { useAdminState } from '../Admin/admin';

/**
 * Defines the initial state object of the admin filters context with all properties set to undefined.
 */
const initialState: AdminFiltersContextState = {
    status: 'initialize',
    appointmentFilters: {
        kioskKey: '',
        dealerKey: undefined,
        name: undefined,
        adminCreated: undefined,
        keySlotEmpty: undefined,
        start: undefined,
        end: undefined,
        storedByAdvisor: undefined,
        keyStatus: undefined,
        sessionId: undefined,
    },
    filterValues: undefined,
    appointments: {
        active: [],
        priority: [],
        dms: [],
    },
    results: null,
    hasPriorityAppointments: false,
};

/**
 * Sets up the admin filters state context and defaults it to undefined so that if any admin filters context hook is used outside of the
 * admin filters state context, we can throw an error.
 */
const AdminFiltersStateContext: AdminFiltersStateContextType = createContext<AdminFiltersState>(undefined);

/**
 * Sets up the admin filters dispatch context and defaults it to undefined so that if any admin filters hook that uses the dispatch context is
 * used outside of the admin filters dispatch context, we can throw an error.
 */
const AdminFiltersDispatchContext: AdminFiltersDispatchContextType = createContext<AdminFiltersDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the admin filters context to update the internal state
 * of the context.
 *
 * @param state - {@link AdminFiltersReducerFunction state} description.
 * @param action - {@link AdminFiltersReducerFunction action} description.
 */
export const adminFiltersReducer: AdminFiltersReducerFunction = (state, action) => {
    switch (action.type) {
        case AdminFiltersDispatchTypes.SetFilters:
            if (state.status !== 'initialize') {
                const { appointmentFilters, filterValues } = action.payload;
                let newAppointmentFilters: AdminFiltersContextState['appointmentFilters'] = appointmentFilters;

                if (filterValues && filterValues.type) {
                    switch (filterValues.type) {
                        case AppointmentType.checkedIn:
                            newAppointmentFilters = { ...appointmentFilters,
                                adminCreated: false };
                            break;
                        case AppointmentType.created:
                            newAppointmentFilters = { ...appointmentFilters,
                                adminCreated: true };
                            break;
                        case AppointmentType.keyDroppedOff:
                            newAppointmentFilters = {
                                ...appointmentFilters,
                                keySlotEmpty: false,
                                storedByAdvisor: false,
                                keyStatus: ['slot_assigned', 'loaner key stored', 'rental key stored', 'reopen requested exchange', 'reopen requested pickup'],
                            };
                            break;
                        case AppointmentType.inKeyBin:
                            newAppointmentFilters = { ...appointmentFilters,
                                keySlotEmpty: false,
                                storedByAdvisor: true,
                                keyStatus: ['slot_assigned'] };
                            break;
                    }
                }
                if (filterValues && filterValues.date) {
                    newAppointmentFilters.start = filterValues.date;
                }
                else if (filterValues && filterValues.dateRange) {
                    newAppointmentFilters.start = filterValues.dateRange.start;
                    newAppointmentFilters.end = filterValues.dateRange.end;
                }

                let results = state.results;
                if (filterValues && filterValues.appointment) {
                    results = [filterValues.appointment];
                }

                return {
                    ...state,
                    filterValues: filterValues,
                    appointmentFilters: newAppointmentFilters,
                    results: !filterValues?.appointment ? null : results,
                    status: !filterValues?.appointment ? 'search' : undefined,
                };
            }
            else {
                return state;
            }
        case AdminFiltersDispatchTypes.SetAppointments:
            const appointments: AdminFiltersContextState['appointments'] = action.payload;
            const results: IAppointment[] = [...appointments.active, ...appointments.dms, ...appointments.priority];

            return {
                ...state,
                appointments: action.payload,
                results: results,
                hasPriorityAppointments: action.payload.priority.length > 0,
                status: undefined,
            };
        case AdminFiltersDispatchTypes.Reinitialize:
            return { ...state,
                status: 'initialize' };
        default:
            return state;
    }
};
// #endregion

// #region Provider
/**
 * Provides the admin filters state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link AdminFiltersProviderProps children} description.
 */
export const AdminFiltersProvider: GoMotoComponent<AdminFiltersProviderProps> = ({
    tabType,
    children,
}) => {
    const { key: kioskKey, dealer: { key: dealerKey } } = useKioskState();
    const { LoginSessionID } = useAdminState();
    const [state, dispatch] = useReducer<AdminFiltersReducerFunction>(adminFiltersReducer, {
        ...initialState,
        appointmentFilters: {
            kioskKey: kioskKey,
            dealerKey: dealerKey,
            keySlotEmpty: tabType === 'check-ins',
            sessionId: LoginSessionID,
        },

    });

    return (
        <AdminFiltersStateContext.Provider value={state}>
            <AdminFiltersDispatchContext.Provider value={dispatch}>
                {children}
            </AdminFiltersDispatchContext.Provider>
        </AdminFiltersStateContext.Provider>
    );
};
// #endregion

// #region useAdminFiltersState
/**
 * AdminFilters context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the AdminFilters context.
 */
export const useAdminFiltersState: AdminFiltersStateHook = () => {
    const context = useContext(AdminFiltersStateContext);

    if (context === undefined) {
        throw new Error('useAdminFiltersState must be used within an AdminFiltersProvider');
    }

    return context;
};
// #endregion

// #region useAdminFiltersDispatch
/**
 * AdminFilters context hook that returns a dispatch function which can dispatch actions to
 * update the state of the AdminFilters context, or throws an error if not used in a
 * component that is a child of the AdminFilters context.
 */
export const useAdminFiltersDispatch: AdminFiltersDispatchHook = () => {
    const context = useContext(AdminFiltersDispatchContext);

    if (context === undefined) {
        throw new Error('useAdminFiltersDispatch must be used within an AdminFiltersProvider');
    }

    return context;
};
// #endregion

// #region useAdminFiltersFetchAppointments
/**
 * Returns an object with functions to get appointments to display on the Check-Ins and Loaded Keys tabs.
 */
export const useAdminFiltersFetchAppointments: AdminFiltersFetchAppointmentsHook = () => {
    const { LoginSessionID } = useAdminState();
    const { appointmentFilters, filterValues } = useAdminFiltersState();
    const adminFilterDispatch = useAdminFiltersDispatch();

    return {
        fetchAppointments: useCallback(async (tabType) => {
            /** Whether we should get active appointments from the back-end, based on if there are no filter types and the tab */
            const shouldGetActive: boolean = !filterValues?.type || [AppointmentType.checkedIn, AppointmentType.created].includes(filterValues.type) || tabType === 'loaded-keys';
            /** Whether we should get priority appointments from the back-end, based on the filter type and tab */
            const shouldGetPriority: boolean = filterValues?.type === AppointmentType.priority || (!filterValues?.type && tabType === 'check-ins');
            /** Whether we should get dms appointments from the back-end, based on the filter type and tab */
            const shouldGetDms: boolean = filterValues?.type === AppointmentType.dmsAppointment || (!filterValues?.type && tabType === 'check-ins');

            const [active, priority, dms] = await Promise.all([
                shouldGetActive ? getActiveAppointments({ ...appointmentFilters,
                    sessionId: LoginSessionID }) : undefined,
                shouldGetPriority ? getPriorityAppointments({ ...appointmentFilters,
                    sessionId: LoginSessionID }) : undefined,
                shouldGetDms ? getDmsAppointments(appointmentFilters) : undefined,
            ])
                .then(([active, priority, dms]) => {
                    const newDms: IAppointment[] | undefined = dms?.map((appointment: IAppointment) => {
                        if (appointment.key) {
                            appointment.dmsAppointmentKey = appointment.key;
                            appointment.dmsAppointmentId = appointment.id;
                            appointment.key = '';
                        }

                        return appointment;
                    });

                    return [active, priority, newDms];
                });

            adminFilterDispatch({
                type: AdminFiltersDispatchTypes.SetAppointments,
                payload: {
                    active: active ?? [],
                    priority: priority ?? [],
                    dms: dms ?? [],
                },
            });
        }, [LoginSessionID, adminFilterDispatch, appointmentFilters, filterValues?.type]),
    };
};
// #endregion